import React, {
  Fragment,
  forwardRef,
  useContext,
  useEffect,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { PortalContext } from "./PortalContext";

export const setRef = (
  ref: React.ForwardedRef<HTMLElement>,
  value: HTMLElement | null,
) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
};

/* eslint-disable @typescript-eslint/prefer-optional-chain */
const canUseDOM = !!(
  typeof window !== "undefined" &&
  window.document &&
  window.document.createElement
);
/* eslint-enable @typescript-eslint/prefer-optional-chain */

export interface PortalProps {
  node?: HTMLElement | null;
  /**
   * Portalがレンダリングされたときに発火します。
   */
  onRender?: () => void;
  children?: React.ReactNode;
  /**
   * Portalの挙動を無効化します。子要素は親のDOMヒエラルキーの中に留まります。
   */
  disablePortal?: boolean;
}

export const Portal = forwardRef<HTMLElement, PortalProps>(
  ({ node: propsNode, onRender, children, disablePortal }, ref) => {
    const [node, setNode] = useState<HTMLElement | null>(null);

    if (!canUseDOM) {
      return null;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const contextNode = useContext(PortalContext);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      setNode(propsNode || contextNode || document.body);
    }, [contextNode, propsNode]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (node) {
        setRef(ref, node);
      }
      return () => {
        if (node) {
          setRef(ref, null);
        }
      };
    }, [propsNode, node, ref]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (node && onRender) {
        onRender();
      }
    }, [node, onRender]);

    return (
      node &&
      (disablePortal ? (
        <Fragment>{children}</Fragment>
      ) : (
        ReactDOM.createPortal(children, node)
      ))
    );
  },
);
